// Import React dependencies
import React, { createContext, useContext, useEffect, useState } from "react";

// Define the initial state
const initialState = {
  name: "",
  username: "",
  email: "",
  age: "",
  country: "",
  relationshipType: "",
  interests: [],
  profilePhoto: "",
  theme: "purple",
  joinDate: "",
  chatHistory: []
};

// Create the context
const LoginContext = createContext({
  userData: initialState,
  setUserData: () => { }, // This will be overridden by the provider
});

export const LoginProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [userData, setUserData] = useState(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        console.error("Error parsing user data from localStorage:", e);
        return initialState;
      }
    }
    return initialState;
  });

  const [isLoggedIn, setLoggedIn] = useState(false);
  const [guestData, setGuestUserData] = useState({
    username: "",
    name: "",
    age: "",
    country: "",
    relationshipType: "",
    interests: [],
  });

  // Update localStorage when userData changes
  useEffect(() => {
    if (userData.username) {
      try {
        localStorage.setItem("user", JSON.stringify(userData));
        console.log("User data saved to localStorage:", userData.username);
      } catch (e) {
        console.error("Error saving user data to localStorage:", e);
      }
    }

    // Apply theme when user data changes
    if (userData.theme) {
      document.documentElement.setAttribute('data-theme', userData.theme);
    }
  }, [userData]);

  // Function to update specific user data fields
  const updateUserData = (newData) => {
    setUserData(prevData => {
      const updatedData = { ...prevData, ...newData };
      localStorage.setItem("user", JSON.stringify(updatedData));
      return updatedData;
    });
  };

  const logoutUser = () => {
    setLoggedIn(false);
    setUserData(initialState);
    localStorage.removeItem("user");
    // Reset theme to default
    document.documentElement.setAttribute('data-theme', 'purple');
  };

  return (
    <LoginContext.Provider
      value={{
        userData,
        setUserData,
        updateUserData,
        isLoggedIn,
        setLoggedIn,
        guestData,
        setGuestUserData,
        logoutUser,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

// Hook to use the login context
export const useLogin = () => {
  const context = useContext(LoginContext);
  if (context === undefined) {
    throw new Error("useLogin must be used within a LoginProvider");
  }
  return context;
};
